/**
 * AdminServices - Panel de Administración de Servicios
 * Monitoreo y control de servicios PM2 (Backend, Frontend, etc.)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  // ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  RestartAlt as RestartIcon,
  Terminal as LogsIcon,
  CheckCircle as HealthyIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Memory as MemoryIcon,
  Speed as CpuIcon,
  Timer as UptimeIcon,
  Replay as RestartsIcon,
  Storage as BackendIcon,
  Web as FrontendIcon,
  SmartToy as AIIcon,
  WhatsApp as WhatsAppIcon,
  Phone as TwilioIcon,
  Settings as PM2Icon,
  Schedule as ScheduleIcon,
  NotificationsActive as ReminderIcon,
  AutoDelete as TimeoutIcon,
  CreditCard as SubscriptionIcon,
  AccessTime as NextRunIcon,
  DataObject as DatabaseIcon,
  Hub as ConnectionsIcon,
  Computer as ServerIcon,
  SdStorage as DiskIcon,
  Security as FirewallIcon,
  VpnKey as PortIcon,
  TrendingUp as LoadIcon,
} from '@mui/icons-material';
// import { useNavigate } from 'react-router-dom';

const API_URL = '/api';

// Colores y gradientes por servicio
const SERVICE_STYLES = {
  'nara-backend': {
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    shadowColor: 'rgba(99, 102, 241, 0.4)',
    icon: BackendIcon,
  },
  'nara-frontend': {
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    shadowColor: 'rgba(14, 165, 233, 0.4)',
    icon: FrontendIcon,
  },
};

const HEALTH_STYLES = {
  openai: { icon: AIIcon, label: 'OpenAI', color: '#10a37f' },
  anthropic: { icon: AIIcon, label: 'Anthropic', color: '#d4a27f' },
  twilio: { icon: TwilioIcon, label: 'Twilio', color: '#f22f46' },
  whatsapp: { icon: WhatsAppIcon, label: 'WhatsApp', color: '#25D366' },
  postgresql: { icon: DatabaseIcon, label: 'PostgreSQL', color: '#336791' },
  pm2: { icon: PM2Icon, label: 'PM2', color: '#2B037A' },
};

// Componente de tarjeta de servicio
const ServiceCard = ({ service, onRestart, onStop, onStart, onViewLogs, loading }) => {
  const styles = SERVICE_STYLES[service.name] || {
    gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
    shadowColor: 'rgba(100, 116, 139, 0.4)',
    icon: BackendIcon,
  };
  const Icon = styles.icon;

  const isOnline = service.status === 'online';
  const isStopped = service.status === 'stopped';

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: isOnline ? styles.gradient : '#94a3b8',
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px rgba(0,0,0,0.12), 0 8px 16px ${styles.shadowColor}`,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              background: isOnline ? styles.gradient : '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${isOnline ? styles.shadowColor : 'rgba(148, 163, 184, 0.4)'}`,
            }}
          >
            <Icon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
              {service.name.replace('nara-', '').charAt(0).toUpperCase() +
                service.name.replace('nara-', '').slice(1)}
            </Typography>
            <Chip
              label={service.status}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: isOnline ? 'rgba(34, 197, 94, 0.15)' : isStopped ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                color: isOnline ? '#16a34a' : isStopped ? '#dc2626' : '#d97706',
              }}
            />
          </Box>
        </Box>

        {/* Status indicator */}
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: isOnline ? '#22c55e' : isStopped ? '#ef4444' : '#f59e0b',
            boxShadow: isOnline
              ? '0 0 8px rgba(34, 197, 94, 0.6)'
              : isStopped
              ? '0 0 8px rgba(239, 68, 68, 0.6)'
              : '0 0 8px rgba(245, 158, 11, 0.6)',
            animation: isOnline ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 },
            },
          }}
        />
      </Box>

      {/* Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MemoryIcon sx={{ fontSize: 16, color: '#64748b' }} />
          <Typography sx={{ fontSize: 13, color: '#475569' }}>{service.memory || 'N/A'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CpuIcon sx={{ fontSize: 16, color: '#64748b' }} />
          <Typography sx={{ fontSize: 13, color: '#475569' }}>{service.cpu || 'N/A'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UptimeIcon sx={{ fontSize: 16, color: '#64748b' }} />
          <Typography sx={{ fontSize: 13, color: '#475569' }}>{service.uptime || 'N/A'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestartsIcon sx={{ fontSize: 16, color: '#64748b' }} />
          <Typography sx={{ fontSize: 13, color: '#475569' }}>{service.restarts} restarts</Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {isOnline ? (
          <>
            <Tooltip title="Reiniciar">
              <IconButton
                size="small"
                onClick={() => onRestart(service.name)}
                disabled={loading}
                sx={{
                  bgcolor: 'rgba(99, 102, 241, 0.1)',
                  color: '#6366f1',
                  '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.2)' },
                }}
              >
                <RestartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Detener">
              <IconButton
                size="small"
                onClick={() => onStop(service.name)}
                disabled={loading}
                sx={{
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                }}
              >
                <StopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Iniciar">
            <IconButton
              size="small"
              onClick={() => onStart(service.name)}
              disabled={loading}
              sx={{
                bgcolor: 'rgba(34, 197, 94, 0.1)',
                color: '#22c55e',
                '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)' },
              }}
            >
              <StartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Ver logs">
          <IconButton
            size="small"
            onClick={() => onViewLogs(service.name)}
            sx={{
              bgcolor: 'rgba(100, 116, 139, 0.1)',
              color: '#64748b',
              '&:hover': { bgcolor: 'rgba(100, 116, 139, 0.2)' },
            }}
          >
            <LogsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// Componente de health check
const HealthCheckCard = ({ checks }) => {
  if (!checks) return null;

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1e293b', mb: 2 }}>
        Health Checks
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {Object.entries(checks).map(([key, value]) => {
          // Skip service checks (shown in cards)
          if (key.startsWith('nara-')) return null;

          const style = HEALTH_STYLES[key] || { icon: HealthyIcon, label: key, color: '#64748b' };
          const Icon = style.icon;
          const isOk = value.status === 'ok';
          const isWarning = value.status === 'warning';

          return (
            <Tooltip key={key} title={value.message || ''}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: isOk
                    ? 'rgba(34, 197, 94, 0.1)'
                    : isWarning
                    ? 'rgba(245, 158, 11, 0.1)'
                    : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${
                    isOk ? 'rgba(34, 197, 94, 0.3)' : isWarning ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)'
                  }`,
                }}
              >
                <Icon sx={{ fontSize: 16, color: style.color }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
                  {style.label}
                </Typography>
                {isOk ? (
                  <HealthyIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                ) : isWarning ? (
                  <WarningIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

// Estilos para jobs programados
const JOB_STYLES = {
  'generar_recordatorios': {
    icon: ReminderIcon,
    label: 'Recordatorios de Citas',
    description: 'Envía recordatorios 24h y 2h antes de cada cita',
    color: '#0ea5e9',
  },
  'verificar_timeouts': {
    icon: TimeoutIcon,
    label: 'Timeouts de Sesiones',
    description: 'Cierra sesiones inactivas por más de 5 minutos',
    color: '#f59e0b',
  },
  'renovar_suscripciones': {
    icon: SubscriptionIcon,
    label: 'Renovación de Suscripciones',
    description: 'Reinicia contadores el día 1 de cada mes',
    color: '#8b5cf6',
  },
};

// Componente de tareas programadas
const ScheduledJobsCard = ({ scheduler, loading }) => {
  if (loading) {
    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: scheduler?.running
            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              background: scheduler?.running
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: scheduler?.running
                ? '0 4px 12px rgba(16, 185, 129, 0.4)'
                : '0 4px 12px rgba(239, 68, 68, 0.4)',
            }}
          >
            <ScheduleIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
              Tareas Programadas
            </Typography>
            <Chip
              label={scheduler?.running ? 'Activo' : 'Detenido'}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: scheduler?.running ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                color: scheduler?.running ? '#16a34a' : '#dc2626',
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: scheduler?.running ? '#22c55e' : '#ef4444',
            boxShadow: scheduler?.running
              ? '0 0 8px rgba(34, 197, 94, 0.6)'
              : '0 0 8px rgba(239, 68, 68, 0.6)',
            animation: scheduler?.running ? 'pulse 2s infinite' : 'none',
          }}
        />
      </Box>

      {/* Jobs list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {scheduler?.jobs && scheduler.jobs.length > 0 ? (
          scheduler.jobs.map((job) => {
            const style = JOB_STYLES[job.id] || {
              icon: ScheduleIcon,
              label: job.name || job.id,
              description: '',
              color: '#64748b',
            };
            const Icon = style.icon;

            // Format next run time
            let nextRunText = 'N/A';
            if (job.next_run) {
              try {
                const nextRun = new Date(job.next_run);
                const now = new Date();
                const diffMs = nextRun - now;
                const diffMins = Math.round(diffMs / 60000);

                if (diffMins < 1) {
                  nextRunText = 'En breve';
                } else if (diffMins < 60) {
                  nextRunText = `En ${diffMins} min`;
                } else if (diffMins < 1440) {
                  nextRunText = `En ${Math.round(diffMins / 60)}h`;
                } else {
                  nextRunText = nextRun.toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                  });
                }
              } catch {
                nextRunText = job.next_run;
              }
            }

            return (
              <Box
                key={job.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(241, 245, 249, 0.8)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(241, 245, 249, 1)',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: `${style.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: style.color }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                    {style.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: '#64748b',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {style.description}
                  </Typography>
                </Box>

                <Tooltip title={`Próxima ejecución: ${job.next_run || 'N/A'}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(99, 102, 241, 0.1)',
                    }}
                  >
                    <NextRunIcon sx={{ fontSize: 12, color: '#6366f1' }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#6366f1' }}>
                      {nextRunText}
                    </Typography>
                  </Box>
                </Tooltip>

                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: job.status === 'active' ? '#22c55e' : '#94a3b8',
                    boxShadow: job.status === 'active' ? '0 0 6px rgba(34, 197, 94, 0.5)' : 'none',
                  }}
                />
              </Box>
            );
          })
        ) : (
          <Typography sx={{ color: '#64748b', textAlign: 'center', py: 2 }}>
            No hay tareas programadas
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Componente de Base de Datos
const DatabaseCard = ({ database, onRestart, onStop, onStart, loading, actionLoading }) => {
  if (loading) {
    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  const isOnline = database?.status === 'online';

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: isOnline
            ? 'linear-gradient(135deg, #336791 0%, #1d4ed8 100%)'
            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              background: isOnline
                ? 'linear-gradient(135deg, #336791 0%, #1d4ed8 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isOnline
                ? '0 4px 12px rgba(51, 103, 145, 0.4)'
                : '0 4px 12px rgba(239, 68, 68, 0.4)',
            }}
          >
            <DatabaseIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
              PostgreSQL
            </Typography>
            <Chip
              label={database?.status || 'unknown'}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: isOnline ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                color: isOnline ? '#16a34a' : '#dc2626',
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: isOnline ? '#22c55e' : '#ef4444',
            boxShadow: isOnline
              ? '0 0 8px rgba(34, 197, 94, 0.6)'
              : '0 0 8px rgba(239, 68, 68, 0.6)',
            animation: isOnline ? 'pulse 2s infinite' : 'none',
          }}
        />
      </Box>

      {/* Info */}
      {isOnline && database?.version && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>
            {database.version}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ConnectionsIcon sx={{ fontSize: 14, color: '#336791' }} />
              <Typography sx={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
                {database.connections || 0} conexiones
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {isOnline ? (
          <>
            <Tooltip title="Reiniciar PostgreSQL">
              <IconButton
                size="small"
                onClick={onRestart}
                disabled={actionLoading}
                sx={{
                  bgcolor: 'rgba(51, 103, 145, 0.1)',
                  color: '#336791',
                  '&:hover': { bgcolor: 'rgba(51, 103, 145, 0.2)' },
                }}
              >
                <RestartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Detener PostgreSQL (Precaución)">
              <IconButton
                size="small"
                onClick={onStop}
                disabled={actionLoading}
                sx={{
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                }}
              >
                <StopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Iniciar PostgreSQL">
            <IconButton
              size="small"
              onClick={onStart}
              disabled={actionLoading}
              sx={{
                bgcolor: 'rgba(34, 197, 94, 0.1)',
                color: '#22c55e',
                '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)' },
              }}
            >
              <StartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// Componente de WhatsApp Status
const WhatsAppCard = ({ whatsapp, loading }) => {
  if (loading) {
    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  const isOnline = whatsapp?.status === 'online';
  const isConfigured = whatsapp?.status !== 'not_configured';

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: isOnline
            ? 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)'
            : !isConfigured
            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              background: isOnline
                ? 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)'
                : !isConfigured
                ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isOnline
                ? '0 4px 12px rgba(37, 211, 102, 0.4)'
                : '0 4px 12px rgba(239, 68, 68, 0.4)',
            }}
          >
            <WhatsAppIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
              WhatsApp
            </Typography>
            <Chip
              label={isOnline ? 'Conectado' : !isConfigured ? 'No configurado' : 'Error'}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: isOnline
                  ? 'rgba(34, 197, 94, 0.15)'
                  : !isConfigured
                  ? 'rgba(245, 158, 11, 0.15)'
                  : 'rgba(239, 68, 68, 0.15)',
                color: isOnline ? '#16a34a' : !isConfigured ? '#d97706' : '#dc2626',
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: isOnline ? '#22c55e' : !isConfigured ? '#f59e0b' : '#ef4444',
            boxShadow: isOnline
              ? '0 0 8px rgba(34, 197, 94, 0.6)'
              : '0 0 8px rgba(239, 68, 68, 0.6)',
            animation: isOnline ? 'pulse 2s infinite' : 'none',
          }}
        />
      </Box>

      {/* Info */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {whatsapp?.whatsapp_number && whatsapp.whatsapp_number !== 'No configurado' && (
          <Typography sx={{ fontSize: 12, color: '#475569' }}>
            <strong>Número:</strong> {whatsapp.whatsapp_number}
          </Typography>
        )}
        {whatsapp?.account_name && (
          <Typography sx={{ fontSize: 12, color: '#475569' }}>
            <strong>Cuenta:</strong> {whatsapp.account_name}
          </Typography>
        )}
        <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>
          {whatsapp?.message || 'Estado desconocido'}
        </Typography>
      </Box>
    </Box>
  );
};

// Componente de Recursos del Sistema
const SystemResourcesCard = ({ resources, loading }) => {
  if (loading) {
    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          gridColumn: { xs: 'span 1', md: 'span 2' },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  const getUsageColor = (percent) => {
    if (percent >= 90) return '#ef4444';
    if (percent >= 70) return '#f59e0b';
    return '#22c55e';
  };

  const ResourceBar = ({ label, icon: Icon, percent, used, total, color }) => (
    <Box sx={{ flex: 1, minWidth: 120 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Icon sx={{ fontSize: 14, color: color || '#64748b' }} />
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(0,0,0,0.1)',
          overflow: 'hidden',
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.min(percent, 100)}%`,
            borderRadius: 4,
            bgcolor: getUsageColor(percent),
            transition: 'width 0.5s ease',
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 10, color: '#64748b' }}>
        {used} / {total} ({percent}%)
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden',
        gridColumn: { xs: 'span 1', md: 'span 2' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
            }}
          >
            <ServerIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
              Recursos del Sistema
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
              Uptime: {resources?.uptime?.formatted || 'N/A'}
            </Typography>
          </Box>
        </Box>

        {/* Load Average */}
        {resources?.load_average && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: 2, bgcolor: 'rgba(99, 102, 241, 0.1)' }}>
            <LoadIcon sx={{ fontSize: 14, color: '#6366f1' }} />
            <Typography sx={{ fontSize: 11, color: '#6366f1', fontWeight: 600 }}>
              Load: {resources.load_average['1min']} / {resources.load_average['5min']} / {resources.load_average['15min']}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Resource Bars */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* CPU */}
        {resources?.cpu && (
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <CpuIcon sx={{ fontSize: 14, color: '#6366f1' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
                CPU
              </Typography>
            </Box>
            <Box
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(0,0,0,0.1)',
                overflow: 'hidden',
                mb: 0.5,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.min(resources.cpu.usage_approx, 100)}%`,
                  borderRadius: 4,
                  bgcolor: getUsageColor(resources.cpu.usage_approx),
                  transition: 'width 0.5s ease',
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 10, color: '#64748b' }}>
              {resources.cpu.cores} cores ({resources.cpu.usage_approx}%)
            </Typography>
          </Box>
        )}

        {/* Memory */}
        {resources?.memory && (
          <ResourceBar
            label="RAM"
            icon={MemoryIcon}
            percent={resources.memory.percent_used}
            used={resources.memory.used_formatted}
            total={resources.memory.total_formatted}
            color="#0ea5e9"
          />
        )}

        {/* Disk */}
        {resources?.disk && (
          <ResourceBar
            label="Disco"
            icon={DiskIcon}
            percent={resources.disk.percent_used}
            used={resources.disk.used_formatted}
            total={resources.disk.total_formatted}
            color="#10b981"
          />
        )}
      </Box>
    </Box>
  );
};

// Componente de Firewall
const FirewallCard = ({ firewall, loading }) => {
  if (loading) {
    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  const isActive = firewall?.status === 'active';

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
        border: '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: isActive
            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 3,
              background: isActive
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isActive
                ? '0 4px 12px rgba(16, 185, 129, 0.4)'
                : '0 4px 12px rgba(239, 68, 68, 0.4)',
            }}
          >
            <FirewallIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
              Firewall (UFW)
            </Typography>
            <Chip
              label={isActive ? 'Activo' : firewall?.status || 'Inactivo'}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: isActive ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                color: isActive ? '#16a34a' : '#dc2626',
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: isActive ? '#22c55e' : '#ef4444',
            boxShadow: isActive
              ? '0 0 8px rgba(34, 197, 94, 0.6)'
              : '0 0 8px rgba(239, 68, 68, 0.6)',
            animation: isActive ? 'pulse 2s infinite' : 'none',
          }}
        />
      </Box>

      {/* Rules */}
      {firewall?.rules && firewall.rules.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>
            {firewall.rules_count} reglas configuradas
          </Typography>
          {firewall.rules.slice(0, 6).map((rule, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: 'rgba(241, 245, 249, 0.8)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
              }}
            >
              <PortIcon sx={{ fontSize: 12, color: '#10b981' }} />
              <Typography sx={{ fontSize: 11, color: '#1e293b', fontWeight: 600, minWidth: 60 }}>
                {rule.port}
              </Typography>
              <Chip
                label={rule.action}
                size="small"
                sx={{
                  height: 16,
                  fontSize: 9,
                  fontWeight: 600,
                  bgcolor: rule.action === 'ALLOW' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  color: rule.action === 'ALLOW' ? '#16a34a' : '#dc2626',
                }}
              />
              <Typography sx={{ fontSize: 10, color: '#64748b', ml: 'auto' }}>
                {rule.from}
              </Typography>
            </Box>
          ))}
          {firewall.rules.length > 6 && (
            <Typography sx={{ fontSize: 10, color: '#64748b', textAlign: 'center' }}>
              +{firewall.rules.length - 6} reglas más
            </Typography>
          )}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          No hay reglas configuradas
        </Typography>
      )}
    </Box>
  );
};

// Dialog de logs
const LogsDialog = ({ open, onClose, serviceName, logs, loading }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{
      sx: {
        bgcolor: '#1e293b',
        color: '#e2e8f0',
        borderRadius: 3,
      },
    }}
  >
    <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LogsIcon />
        <Typography sx={{ fontWeight: 600 }}>Logs: {serviceName}</Typography>
      </Box>
    </DialogTitle>
    <DialogContent sx={{ p: 0 }}>
      <Box
        sx={{
          fontFamily: 'monospace',
          fontSize: 12,
          p: 2,
          maxHeight: 400,
          overflow: 'auto',
          bgcolor: '#0f172a',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : logs && logs.length > 0 ? (
          logs.map((log, idx) => (
            <Box
              key={idx}
              sx={{
                py: 0.25,
                color: log.type === 'err' ? '#f87171' : '#94a3b8',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {log.message}
            </Box>
          ))
        ) : (
          <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>
            No hay logs disponibles
          </Typography>
        )}
      </Box>
    </DialogContent>
    <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', px: 2, py: 1 }}>
      <Button onClick={onClose} sx={{ color: '#94a3b8' }}>
        Cerrar
      </Button>
    </DialogActions>
  </Dialog>
);

// Componente principal
export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [health, setHealth] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [database, setDatabase] = useState(null);
  const [whatsapp, setWhatsapp] = useState(null);
  const [systemResources, setSystemResources] = useState(null);
  const [firewall, setFirewall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Logs dialog
  const [logsDialog, setLogsDialog] = useState({ open: false, service: '', logs: [], loading: false });

  // Fetch status
  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, healthRes, schedulerRes, databaseRes, whatsappRes, resourcesRes, firewallRes] = await Promise.all([
        fetch(`${API_URL}/admin/services/status`),
        fetch(`${API_URL}/admin/services/health`),
        fetch(`${API_URL}/admin/services/scheduler`),
        fetch(`${API_URL}/admin/services/database/status`),
        fetch(`${API_URL}/admin/services/whatsapp/status`),
        fetch(`${API_URL}/admin/services/system/resources`),
        fetch(`${API_URL}/admin/services/firewall/status`),
      ]);

      if (statusRes.ok) {
        const data = await statusRes.json();
        setServices(data.services || []);
      }

      if (healthRes.ok) {
        const data = await healthRes.json();
        setHealth(data);
      }

      if (schedulerRes.ok) {
        const data = await schedulerRes.json();
        setScheduler(data);
      }

      if (databaseRes.ok) {
        const data = await databaseRes.json();
        setDatabase(data);
      }

      if (resourcesRes.ok) {
        const data = await resourcesRes.json();
        setSystemResources(data);
      }

      if (firewallRes.ok) {
        const data = await firewallRes.json();
        setFirewall(data);
      }

      if (whatsappRes.ok) {
        const data = await whatsappRes.json();
        setWhatsapp(data);
      }

      setError(null);
    } catch (err) {
      setError('Error conectando con el servidor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Service actions
  const handleServiceAction = async (action, serviceName) => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API_URL}/admin/services/${serviceName}/${action}`, {
        method: 'POST',
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(data.message);
        setTimeout(() => fetchStatus(), 1000);
      } else {
        setError(data.detail || 'Error ejecutando acción');
      }
    } catch (err) {
      setError('Error conectando con el servidor');
    } finally {
      setActionLoading(false);
    }
  };

  // View logs
  const handleViewLogs = async (serviceName) => {
    setLogsDialog({ open: true, service: serviceName, logs: [], loading: true });

    try {
      const res = await fetch(`${API_URL}/admin/services/${serviceName}/logs?lines=100`);
      if (res.ok) {
        const data = await res.json();
        setLogsDialog((prev) => ({ ...prev, logs: data.logs || [], loading: false }));
      } else {
        setLogsDialog((prev) => ({ ...prev, logs: [], loading: false }));
      }
    } catch (err) {
      setLogsDialog((prev) => ({ ...prev, logs: [], loading: false }));
    }
  };

  // Database actions
  const handleDatabaseAction = async (action) => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API_URL}/admin/services/database/${action}`, {
        method: 'POST',
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(data.message);
        setTimeout(() => fetchStatus(), 2000);
      } else {
        setError(data.detail || 'Error ejecutando acción en base de datos');
      }
    } catch (err) {
      setError('Error conectando con el servidor');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f1f5f9',
        py: 3,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: 24 }}>
              Admin: Servicios
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 14 }}>
              Monitoreo y control de servicios PM2
            </Typography>
          </Box>
          <Button
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={fetchStatus}
            disabled={loading}
            sx={{
              color: '#1e3a5f',
              borderColor: '#cbd5e1',
              '&:hover': { borderColor: '#1e3a5f', bgcolor: 'rgba(30, 58, 95, 0.05)' },
            }}
            variant="outlined"
            size="small"
          >
            Actualizar
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Overall status */}
        {health && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
              p: 2,
              borderRadius: 3,
              bgcolor:
                health.status === 'healthy'
                  ? 'rgba(34, 197, 94, 0.15)'
                  : health.status === 'degraded'
                  ? 'rgba(239, 68, 68, 0.15)'
                  : 'rgba(245, 158, 11, 0.15)',
              border: `1px solid ${
                health.status === 'healthy'
                  ? 'rgba(34, 197, 94, 0.3)'
                  : health.status === 'degraded'
                  ? 'rgba(239, 68, 68, 0.3)'
                  : 'rgba(245, 158, 11, 0.3)'
              }`,
            }}
          >
            {health.status === 'healthy' ? (
              <HealthyIcon sx={{ color: '#22c55e', fontSize: 28 }} />
            ) : health.status === 'degraded' ? (
              <ErrorIcon sx={{ color: '#ef4444', fontSize: 28 }} />
            ) : (
              <WarningIcon sx={{ color: '#f59e0b', fontSize: 28 }} />
            )}
            <Box>
              <Typography sx={{ color: '#1e293b', fontWeight: 600 }}>
                Estado del sistema:{' '}
                {health.status === 'healthy'
                  ? 'Saludable'
                  : health.status === 'degraded'
                  ? 'Degradado'
                  : 'Con advertencias'}
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: 12 }}>
                Última actualización: {new Date(health.timestamp).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Section: System Resources */}
        <Typography
          sx={{
            color: '#94a3b8',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            mb: 1.5,
          }}
        >
          Recursos del Sistema
        </Typography>
        <Box sx={{ mb: 4 }}>
          <SystemResourcesCard resources={systemResources} loading={loading && !systemResources} />
        </Box>

        {/* Services grid - PM2, Database, WhatsApp */}
        {loading && services.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : (
          <>
            {/* Section: PM2 Services */}
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 1.5,
              }}
            >
              Servicios PM2
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 3,
                mb: 4,
              }}
            >
              {services.map((service) => (
                <ServiceCard
                  key={service.name}
                  service={service}
                  onRestart={(name) => handleServiceAction('restart', name)}
                  onStop={(name) => handleServiceAction('stop', name)}
                  onStart={(name) => handleServiceAction('start', name)}
                  onViewLogs={handleViewLogs}
                  loading={actionLoading}
                />
              ))}
            </Box>

            {/* Section: Infrastructure */}
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 1.5,
              }}
            >
              Infraestructura
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3,
                mb: 4,
              }}
            >
              {/* Database */}
              <DatabaseCard
                database={database}
                onRestart={() => handleDatabaseAction('restart')}
                onStop={() => handleDatabaseAction('stop')}
                onStart={() => handleDatabaseAction('start')}
                loading={loading && !database}
                actionLoading={actionLoading}
              />

              {/* WhatsApp */}
              <WhatsAppCard whatsapp={whatsapp} loading={loading && !whatsapp} />

              {/* Firewall */}
              <FirewallCard firewall={firewall} loading={loading && !firewall} />
            </Box>
          </>
        )}

        {/* Scheduled Jobs and Health Checks */}
        <Typography
          sx={{
            color: '#94a3b8',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            mb: 1.5,
          }}
        >
          Scheduler y APIs
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3,
          }}
        >
          {/* Scheduled Jobs */}
          <ScheduledJobsCard scheduler={scheduler} loading={loading && !scheduler} />

          {/* Health checks */}
          {health && <HealthCheckCard checks={health.checks} />}
        </Box>

        {/* Logs dialog */}
        <LogsDialog
          open={logsDialog.open}
          onClose={() => setLogsDialog({ ...logsDialog, open: false })}
          serviceName={logsDialog.service}
          logs={logsDialog.logs}
          loading={logsDialog.loading}
        />
      </Container>
    </Box>
  );
}
